/**
 * Capability entity adapter (P15.2 / #416).
 *
 * Capabilities are the layered stack: tier 1 customer outcomes ->
 * tier 2 bank services -> tier 3 platform building blocks. Tier is
 * 1-5 (CHECK constraint at the DB level). Self-FK
 * `parent_capability_id` lets the admin nest a tier-3 capability
 * under its tier-2 parent.
 */
import type { AdminColumn, EntityAdapter, FormField } from "./types";
import { CapabilityDependencyLinks } from "./capabilityLinks";

export interface Capability {
  id: string;
  code: string;
  name: string;
  description: string | null;
  tier: number;
  parent_capability_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface CapabilityIn {
  code: string;
  name: string;
  description: string | null;
  tier: number;
  parent_capability_id: string | null;
}

export type CapabilityPatch = Partial<CapabilityIn>;

const columns: AdminColumn<Capability>[] = [
  {
    key: "code",
    label: "Code",
    sortValue: (r) => r.code,
    render: (r) => <span className="font-mono text-xs text-slate-700">{r.code}</span>,
  },
  {
    key: "name",
    label: "Name",
    sortValue: (r) => r.name,
    render: (r) => <span className="font-medium text-slate-900">{r.name}</span>,
  },
  {
    key: "tier",
    label: "Tier",
    sortValue: (r) => r.tier,
    align: "right",
    render: (r) => (
      <span
        data-testid="capability-tier-cell"
        className="inline-flex items-center justify-center rounded-full bg-slate-100 text-slate-800 w-6 h-6 text-xs font-medium tabular-nums"
      >
        {r.tier}
      </span>
    ),
  },
  {
    key: "parent_capability_id",
    label: "Parent",
    render: (r) =>
      r.parent_capability_id ? (
        <span
          data-testid="capability-parent-cell"
          className="font-mono text-xs text-slate-500"
          title={r.parent_capability_id}
        >
          {r.parent_capability_id.slice(0, 8)}...
        </span>
      ) : (
        <span className="text-xs text-slate-400">--</span>
      ),
  },
];

const formFields: FormField<CapabilityIn>[] = [
  { kind: "text", key: "code", label: "Code", required: true, maxLength: 64 },
  { kind: "text", key: "name", label: "Name", required: true, maxLength: 255 },
  { kind: "textarea", key: "description", label: "Description", rows: 3 },
  { kind: "number", key: "tier", label: "Tier (1-5)", min: 1, max: 5, required: true },
  {
    kind: "parentSelect",
    key: "parent_capability_id",
    label: "Parent capability",
    entityKind: "capability",
    nullable: true,
  },
];

export const capabilityAdapter: EntityAdapter<
  Capability,
  CapabilityIn,
  CapabilityPatch
> = {
  i18nPrefix: "modules.foundation.admin.capabilities",
  importKind: "capability",
  endpoint: "/v1/admin/foundation/capabilities",
  queryKey: ["foundation-admin", "capabilities"] as const,
  columns,
  defaultSort: { key: "code", dir: "asc" },
  rowKey: (r) => r.id,
  rowSearchHaystack: (r) => `${r.code} ${r.name} ${r.description ?? ""}`,
  formFields,
  formInitial: () => ({
    code: "",
    name: "",
    description: null,
    tier: 3,
    parent_capability_id: null,
  }),
  rowToPatch: (r) => ({
    code: r.code,
    name: r.name,
    description: r.description,
    tier: r.tier,
    parent_capability_id: r.parent_capability_id,
  }),
  // D4: a committed capability dependency was visible nowhere at all.
  extraSections: [CapabilityDependencyLinks],
};
